<template>
  <AIBasePanel
    :title="getLocaleText('ai_summary_assistant_panel.title')"
    @close="close"
    @home="home"
  >
    <template #toolbarPrependRight>
      <div v-if="showReturnToStream">
        <BaseButton
          circle
          data-testid="return-button"
          flat
          icon="contract"
          new-design
          new-icon
          @click="emit('close')"
        />
        <StreemTooltip>
          {{ getLocaleText("ai_summary_panel.return_to_stream") }}
        </StreemTooltip>
      </div>
    </template>

    <AISummaryStreamResponse
      v-if="stream"
      :stream-id="stream.id"
    />

    <template
      v-if="aiStreamSummary.result && stream"
      #footer
    >
      <AISummaryStreamActions :stream-id="stream.id" />
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";
import { useRoute } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import AISummaryStreamActions from "shared/components/AI/AISummary/AISummaryStreamActions.vue";
import AISummaryStreamResponse from "shared/components/AI/AISummary/AISummaryStreamResponse.vue";
import { BaseButton, StreemTooltip } from "shared/components/base";
import useAIStore from "shared/stores/ai";
import useStreamsStore from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";

const route = useRoute();

const emit = defineEmits(["close", "home"]);

const aiStore = useAIStore();
const { aiSummaryStreamId, aiStreamSummary } = storeToRefs(aiStore);

const streamsStore = useStreamsStore();
const { getStreamById } = streamsStore;

const stream = ref<Nullable<Stream>>(null);
const showReturnToStream = ref<boolean>(true);

function home() {
  aiSummaryStreamId.value = null;
  emit("home");
}

function close() {
  aiSummaryStreamId.value = null;
  emit("close");
}

watch(
  aiSummaryStreamId,
  () => {
    if (!aiSummaryStreamId.value) {
      return;
    }

    const streamFromStore = getStreamById(aiSummaryStreamId.value);

    if (!streamFromStore) {
      return;
    }

    stream.value = streamFromStore;
  },
  { immediate: true }
);

watch(
  () => route.params.groupSlug,
  (value) => {
    if (stream.value?.group.slug !== value) {
      aiSummaryStreamId.value = null;
    }
  }
);
</script>

<style lang="scss">
@import "shared/css/components/ai.scss";
@import "shared/css/utils/transitions/fade";

:root {
  --fade-duration: 0.3s;
}
</style>
